/**
 * ADR-0242 — bundled brand marks: which registry agent gets which mark.
 *
 * Marks come from `simple-icons` (CC0 SVG data; brand owners keep their
 * trademark rights — display is nominative: the mark names who visited).
 * Brands simple-icons no longer carries (OpenAI, Microsoft/Bing, Amazon,
 * Yandex, Ahrefs, Moz, Common Crawl …) are NOT redrawn from elsewhere:
 * they get the operator's own site icon through Sill's API (fetched,
 * validated and cached server-side), else a monogram tile.
 *
 * Matching is by EXACT agent id, through the bundled registry's
 * `organization` (ADR-0060 floor) plus a short list of crawlers the
 * dashboard names from the UA alone. A prefix match would let a new
 * registry id such as `agent_openai_lookalike` wear a brand; an exact id
 * cannot.
 *
 * Static named imports keep the bundle tree-shakeable (the whole library
 * is ~3000 icons).
 */

import { Linkedin, Slack, type LucideIcon } from 'lucide-react';
import {
  siAnthropic,
  siApple,
  siBaidu,
  siBytedance,
  siDiscord,
  siDuckduckgo,
  siFacebook,
  siGithub,
  siGoogle,
  siGooglegemini,
  siHuawei,
  siMeta,
  siMistralai,
  siPerplexity,
  siPingdom,
  siSemrush,
  siSogou,
  siX,
} from 'simple-icons';

import { BUNDLED_REGISTRY } from '@sill/types';

export type SimpleIcon = { readonly title: string; readonly path: string; readonly hex: string };

export type BrandMark =
  | { readonly kind: 'simple'; readonly icon: SimpleIcon }
  /** lucide's own brand glyph, drawn in the brand colour. */
  | { readonly kind: 'lucide'; readonly icon: LucideIcon; readonly color: string; readonly title: string };

/** Bundled registry organisation → mark. Absent = no bundled mark. */
const ORG_MARKS: Readonly<Record<string, BrandMark>> = {
  Anthropic: { kind: 'simple', icon: siAnthropic },
  Apple: { kind: 'simple', icon: siApple },
  Baidu: { kind: 'simple', icon: siBaidu },
  ByteDance: { kind: 'simple', icon: siBytedance },
  Discord: { kind: 'simple', icon: siDiscord },
  DuckDuckGo: { kind: 'simple', icon: siDuckduckgo },
  Google: { kind: 'simple', icon: siGoogle },
  'Huawei (Petal Search)': { kind: 'simple', icon: siHuawei },
  Meta: { kind: 'simple', icon: siMeta },
  Mistral: { kind: 'simple', icon: siMistralai },
  Perplexity: { kind: 'simple', icon: siPerplexity },
  Sogou: { kind: 'simple', icon: siSogou },
  'X (Twitter)': { kind: 'simple', icon: siX },
  LinkedIn: { kind: 'lucide', icon: Linkedin, color: '#0A66C2', title: 'LinkedIn' },
  Slack: { kind: 'lucide', icon: Slack, color: '#611F69', title: 'Slack' },
};

/** Per-id exceptions and UA-named crawlers outside the bundled floor. */
const ID_MARKS: Readonly<Record<string, BrandMark>> = {
  agent_google_gemini: { kind: 'simple', icon: siGooglegemini },
  agent_meta_facebook_external: { kind: 'simple', icon: siFacebook },
  agent_meta_facebot: { kind: 'simple', icon: siFacebook },
  agent_semrushbot: { kind: 'simple', icon: siSemrush },
  agent_pingdom: { kind: 'simple', icon: siPingdom },
  agent_github_camo: { kind: 'simple', icon: siGithub },
  agent_duckduckgo_searchbot: { kind: 'simple', icon: siDuckduckgo },
  agent_google_news_bot: { kind: 'simple', icon: siGoogle },
};

const ORG_BY_ID: ReadonlyMap<string, string> = new Map(
  BUNDLED_REGISTRY.map((s) => [s.agent_id as string, s.organization]),
);

/** Registry organisation for a bundled id ("OpenAI"), else null. */
export function bundledOrganisation(agent_id: string | null | undefined): string | null {
  if (agent_id === null || agent_id === undefined) return null;
  return ORG_BY_ID.get(agent_id) ?? null;
}

/** The bundled mark for an exact agent id, or null. Never for Sill ids. */
export function bundledBrandMark(agent_id: string | null | undefined): BrandMark | null {
  if (agent_id === null || agent_id === undefined || agent_id.startsWith('agent_sill_')) return null;
  const byId = ID_MARKS[agent_id];
  if (byId !== undefined) return byId;
  const org = ORG_BY_ID.get(agent_id);
  return org !== undefined ? ORG_MARKS[org] ?? null : null;
}

/** Organisations the bundled marks cover (for the coverage test). */
export const ORGANISATIONS_WITH_MARKS: readonly string[] = Object.keys(ORG_MARKS);
